import type { CampaignInstrument, InstrumentStatus } from '@/features/campaigns/types'
import { campaignDisplayName } from '@/features/campaigns/lib/campaign-list-display'
import {
  resolveInstrumentLaunchStatusPill,
  shouldShowLaunchResponseProgress,
} from '@/features/campaigns/lib/instrument-launch-status'
import { ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS } from './editor-viewer-dashboard-layout'

export type AllocatedInstrumentFilter =
  | 'all'
  | 'active'
  | 'paused'
  | 'completed'
  | 'draft'

export const ALLOCATED_INSTRUMENT_FILTER_OPTIONS: ReadonlyArray<{
  value: AllocatedInstrumentFilter
  label: string
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Live' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'draft', label: 'Draft' },
]

export interface AllocatedInstrumentRow {
  instrument: CampaignInstrument
  campaignId: string
  campaignName: string
}

export function flattenAllocatedInstruments(
  campaigns: ReadonlyArray<{ id: string; name: string }>,
  instrumentsByCampaignId: Map<string, CampaignInstrument[]>,
): AllocatedInstrumentRow[] {
  const rows: AllocatedInstrumentRow[] = []
  for (const campaign of campaigns) {
    const instruments = instrumentsByCampaignId.get(campaign.id) ?? []
    const campaignName = campaignDisplayName(campaign.name)
    for (const instrument of instruments) {
      rows.push({ instrument, campaignId: campaign.id, campaignName })
    }
  }
  return rows
}

export function matchesInstrumentFilter(
  row: AllocatedInstrumentRow,
  filter: AllocatedInstrumentFilter,
): boolean {
  if (filter === 'all') return true
  const { status, closeReason } = row.instrument
  if (filter === 'completed') {
    return (
      status === 'completed' ||
      (status === 'closed' && closeReason === 'target_reached')
    )
  }
  return status === filter
}

function statusPillBorder(
  status: InstrumentStatus,
  closeReason?: CampaignInstrument['closeReason'],
): string {
  switch (status) {
    case 'draft':
      return '#fcd34d'
    case 'paused':
      return '#fecaca'
    case 'completed':
      return '#86efac'
    case 'expired':
      return '#cbd5e1'
    case 'closed':
      if (closeReason === 'target_reached') return '#86efac'
      if (closeReason === 'timeline_expired') return '#cbd5e1'
      return '#cbd5e1'
    case 'active':
    default:
      return '#fecaca'
  }
}

/** Status pill aligned with Sensemaker launching screen labels (Live, Pause, Completed, …). */
export function instrumentStatusPill(
  instrument: Pick<CampaignInstrument, 'status' | 'closeReason'>,
): {
  label: string
  bg: string
  color: string
  border: string
} {
  const launch = resolveInstrumentLaunchStatusPill(instrument.status, instrument.closeReason)
  return {
    ...launch,
    border: statusPillBorder(instrument.status, instrument.closeReason),
  }
}

export function sortAllocatedInstrumentsByUpdatedDesc(
  rows: AllocatedInstrumentRow[],
): AllocatedInstrumentRow[] {
  return [...rows].sort((a, b) =>
    b.instrument.updatedAt.localeCompare(a.instrument.updatedAt),
  )
}

export function sortAllocatedInstrumentsByCampaignName(
  rows: AllocatedInstrumentRow[],
  direction: 'asc' | 'desc',
): AllocatedInstrumentRow[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const cmp = a.campaignName.localeCompare(b.campaignName, undefined, {
      sensitivity: 'base',
    })
    return cmp * factor
  })
}

export function formatResponsesLabel(count: number, target: number): string {
  const safeCount = Math.max(0, count)
  const safeTarget = Math.max(0, target)
  if (safeTarget <= 0) {
    return safeCount.toLocaleString('en-US')
  }
  return `${safeCount.toLocaleString('en-US')} / ${safeTarget.toLocaleString('en-US')}`
}

function isTerminalResponseStatus(instrument: CampaignInstrument): boolean {
  if (instrument.status === 'completed' || instrument.status === 'expired') return true
  if (instrument.status === 'closed') {
    return (
      instrument.closeReason === 'target_reached' ||
      instrument.closeReason === 'timeline_expired'
    )
  }
  return false
}

export function responseProgressForInstrument(instrument: CampaignInstrument): {
  count: number
  target: number
  percent: number
  showBar: boolean
  barColor: string
  labelColor: string
  barShadow: string | undefined
} {
  const count = Math.max(0, instrument.responseCount ?? 0)
  const target = Math.max(0, instrument.responseTarget ?? 0)
  const showBar = shouldShowLaunchResponseProgress(instrument)
  const percent = target > 0 ? Math.min(100, Math.round((count / target) * 100)) : 0

  const { fill, labelColor, completedLabelColor, completedBarShadow, completedBarInsetShadow } =
    ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS

  let barColor: string = fill.active
  let responseLabelColor: string = labelColor
  let barShadow: string | undefined

  if (isTerminalResponseStatus(instrument)) {
    barColor = fill.completed
    responseLabelColor = completedLabelColor
    barShadow = `${completedBarShadow}, ${completedBarInsetShadow}`
  } else if (instrument.status === 'paused') {
    barColor = fill.paused
  } else if (instrument.status === 'active') {
    barColor = fill.active
  }

  return {
    count,
    target,
    percent,
    showBar,
    barColor,
    labelColor: responseLabelColor,
    barShadow,
  }
}

export function filterEmptyMessage(filter: AllocatedInstrumentFilter): string {
  const label =
    ALLOCATED_INSTRUMENT_FILTER_OPTIONS.find((o) => o.value === filter)?.label ?? filter
  return `No instruments found with status: ${label}.`
}

export function deriveEditorViewerDashboardMetrics(
  campaigns: ReadonlyArray<{ id: string }>,
  rows: ReadonlyArray<AllocatedInstrumentRow>,
): { totalAllocatedCampaigns: number; totalActiveInstruments: number } {
  return {
    totalAllocatedCampaigns: campaigns.length,
    totalActiveInstruments: rows.filter((row) => row.instrument.status === 'active').length,
  }
}
